import { useEffect, useState } from 'react';
import { cn } from '../../utils';
import type { ChatBubble } from '../../types';
import styles from './ChatBubbles.module.css';

/** Seconds before the first bubble animates in, so the launcher settles first. */
const BUBBLE_BASE_DELAY = 0.15;
/** Seconds between successive bubbles animating. */
const BUBBLE_STAGGER = 0.15;
/** Seconds each bubble's enter/exit animation runs (must match the CSS duration). */
const BUBBLE_DURATION = 0.45;

export interface ChatBubblesProps {
  bubbles: ChatBubble[];
  onBubbleClick?: (bubble: ChatBubble) => void;
  /** When false, bubbles play their exit animation and then unmount. */
  visible?: boolean;
  className?: string;
}

/** Teaser bubbles above the chat button while the widget is closed. */
export function ChatBubbles({
  bubbles,
  onBubbleClick,
  visible = true,
  className,
}: ChatBubblesProps) {
  const activeBubbles = bubbles.filter((b) => !b.disabled);
  const total = activeBubbles.length;

  // Keep the bubbles in the DOM while they play their exit animation, then remove
  // them once the last one has finished sinking away.
  const [rendered, setRendered] = useState(visible);
  const [wasVisible, setWasVisible] = useState(visible);
  if (visible !== wasVisible) {
    // Adjust state during render (React's recommended pattern over an effect):
    // mount immediately when becoming visible so the enter animation plays.
    setWasVisible(visible);
    if (visible) setRendered(true);
  }
  useEffect(() => {
    if (visible || !rendered) return;
    const exitMs = ((total - 1) * BUBBLE_STAGGER + BUBBLE_DURATION) * 1000 + 50;
    const timeout = setTimeout(() => setRendered(false), exitMs);
    return () => clearTimeout(timeout);
  }, [visible, rendered, total]);

  if (total === 0 || (!visible && !rendered)) {
    return null;
  }

  // Reverse order so newest/most important appears at the bottom (closest to button)
  const reversedBubbles = [...activeBubbles].reverse();

  return (
    <div className={cn(styles.container, className)}>
      {reversedBubbles.map((bubble, index) => {
        // Enter: rise in from the bottom up — the bubble nearest the launcher (last
        // in DOM order) goes first. Exit: the reverse — the top bubble leaves first.
        const delay = visible
          ? BUBBLE_BASE_DELAY + (total - 1 - index) * BUBBLE_STAGGER
          : index * BUBBLE_STAGGER;

        // A bubble can carry its own colors, overriding the default white fill.
        const style = {
          animationDelay: `${delay}s`,
          ...(bubble.bgColor ? { background: bubble.bgColor } : {}),
          ...(bubble.textColor ? { color: bubble.textColor } : {}),
        };

        return (
          <button
            key={bubble.id ?? `bubble-${index}`}
            type="button"
            onClick={() => onBubbleClick?.(bubble)}
            className={cn(styles.bubble, visible ? styles.entering : styles.exiting)}
            style={style}
          >
            {bubble.label}
          </button>
        );
      })}
    </div>
  );
}
